import React, { useState, useRef, useEffect } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Bot,
    Send,
    Sparkles,
    FileText,
    Loader2,
    X,
    Lightbulb,
    RefreshCw
} from 'lucide-react';

/**
 * AICopilotPanel - AI Meeting Assistant
 * Chat interface for asking questions about the meeting
 */
const AICopilotPanel = ({
    chatHistory,
    isLoading,
    onSendMessage,
    onGenerateSummary,
    onGetInsights,
    insights,
    onClose,
    error
}) => {
    const dragControls = useDragControls();
    const [message, setMessage] = useState('');
    const scrollRef = useRef(null);

    // Suggested prompts
    const suggestions = [
        "Summarize the discussion so far",
        "What are the key action items?",
        "What decisions were made?",
        "Who said what about the main topic?",
    ];

    // Auto-scroll on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatHistory]);

    const handleSend = () => {
        if (!message.trim() || isLoading) return;
        onSendMessage(message);
        setMessage('');
    };

    const handleSuggestionClick = (suggestion) => {
        onSendMessage(suggestion);
    };

    return (
        <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="ai-panel ai-panel-mobile w-full md:w-[380px] h-full max-h-[calc(100dvh-140px)] bg-void-paper border-l border-white/5 flex flex-col overflow-hidden shadow-2xl z-50 touch-none"
        >
            {/* Header (Drag Handle) */}
            <div 
                className="p-4 border-b border-white/5 cursor-move active:cursor-grabbing select-none"
                onPointerDown={(e) => dragControls.start(e)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-purple to-neon-cyan flex items-center justify-center">
                            <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold text-white">AI Copilot</h3>
                            <p className="text-xs text-grey-400">Powered by Kautilya AI</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-grey-400 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="p-3 border-b border-white/5 flex gap-2">
                <Button
                    onClick={onGenerateSummary}
                    disabled={isLoading}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10"
                >
                    <FileText className="w-4 h-4 mr-1.5" />
                    Summary
                </Button>
                <Button
                    onClick={onGetInsights}
                    disabled={isLoading}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10"
                >
                    <Lightbulb className="w-4 h-4 mr-1.5" />
                    Insights
                </Button>
            </div>

            {/* Insights Display */}
            {insights && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mx-3 mt-3 p-3 bg-gradient-to-r from-neon-purple/10 to-neon-cyan/10 border border-white/10 rounded-lg"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-neon-purple" />
                        <span className="text-xs font-medium text-grey-200">Live Insights</span>
                        <button
                            onClick={onGetInsights}
                            className="ml-auto text-grey-400 hover:text-white"
                        >
                            <RefreshCw className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="space-y-1.5 text-xs">
                        {insights.main_topic && (
                            <p className="text-grey-300">
                                <span className="text-grey-500">Topic:</span> {insights.main_topic}
                            </p>
                        )}
                        {insights.mood && (
                            <p className="text-grey-300">
                                <span className="text-grey-500">Mood:</span> {insights.mood}
                            </p>
                        )}
                        {insights.suggested_action && (
                            <p className="text-neon-cyan">
                                💡 {insights.suggested_action}
                            </p>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Chat Messages */}
            <div className="flex-1 min-h-0">
                <ScrollArea className="h-full p-4">
                    {chatHistory.length === 0 ? (
                        <div className="space-y-4">
                            <div className="text-center py-6">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center">
                                    <Bot className="w-8 h-8 text-neon-cyan" />
                                </div>
                                <h4 className="text-grey-100 font-medium mb-1">Ask me anything!</h4>
                                <p className="text-xs text-grey-400">
                                    I can help you understand the meeting discussion
                                </p>
                            </div>

                            {/* Suggested Questions */}
                            <div className="space-y-2">
                                <p className="text-xs text-grey-500 mb-2">Try asking:</p>
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-grey-300 hover:text-white transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {chatHistory.map((msg, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                >
                                    {/* Avatar */}
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${msg.role === 'user'
                                        ? 'bg-neon-cyan/20'
                                        : 'bg-gradient-to-br from-neon-purple/30 to-neon-cyan/30'
                                        }`}>
                                        {msg.role === 'user' ? (
                                            <span className="text-xs font-medium text-neon-cyan">You</span>
                                        ) : (
                                            <Bot className="w-4 h-4 text-neon-purple" />
                                        )}
                                    </div>

                                    {/* Message */}
                                    <div className={`flex-1 max-w-[280px] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                        <div className={`inline-block px-4 py-2.5 rounded-2xl ${msg.role === 'user'
                                            ? 'bg-neon-cyan text-black rounded-br-md'
                                            : msg.isError
                                                ? 'bg-red-500/10 text-red-300 border border-red-500/20 rounded-bl-md'
                                                : 'bg-white/5 text-grey-100 rounded-bl-md'
                                            }`}>
                                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}

                            {/* Loading indicator */}
                            {isLoading && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex gap-3"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-purple/30 to-neon-cyan/30 flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-neon-purple" />
                                    </div>
                                    <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white/5">
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            <div ref={scrollRef} />
                        </div>
                    )}
                </ScrollArea>
            </div>

            {/* Input */}
            <div className="p-4 border-t border-white/5">
                <div className="flex gap-2">
                    <Input
                        placeholder="Ask about the meeting..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                        disabled={isLoading}
                        className="input-neon text-sm"
                    />
                    <Button
                        onClick={handleSend}
                        disabled={!message.trim() || isLoading}
                        className="bg-neon-cyan text-black hover:bg-neon-cyan/80 px-3"
                    >
                        {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                    </Button>
                </div>
            </div>
        </motion.div>
    );
};

export default AICopilotPanel;
